// News.js
import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { VariableSizeList } from 'react-window';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Collapse,
  IconButton,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  Grid,
  useTheme,
  useMediaQuery,
  SwipeableDrawer,
  Fab,
  Divider,
  Card,
  CardContent,
  CardHeader,
  CardActions,
  Tooltip,
  Autocomplete,
  Checkbox,
  CircularProgress,
  Pagination,
  TablePagination,
} from '@mui/material';

import { Link } from 'react-router-dom';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import FilterListIcon from '@mui/icons-material/FilterList';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import ReactPlayer from 'react-player';
import { Tweet } from 'react-twitter-widgets';
import parse from 'html-react-parser';
import api from '../api/axiosConfig';
import { getPlayerPhotoUrl } from '../utils/mediaUrl';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { usePositions } from '../utils/positions';

// =====================================================
//  Autocomplete virtualisé  (inchangé)
// =====================================================
const LISTBOX_PADDING = 8;
function renderRow(props) {
  const { data, index, style } = props;
  const item = data[index];
  return React.cloneElement(item, {
    style: { ...style, top: style.top + LISTBOX_PADDING },
  });
}
const OuterElementContext = React.createContext({});
const OuterElementType = React.forwardRef((props, ref) => {
  const outerProps = React.useContext(OuterElementContext);
  return (
    <div
      ref={ref}
      {...props}
      {...outerProps}
      style={{ ...props.style, overflowX: 'hidden', overflowY: 'auto', maxWidth: '100%' }}
    />
  );
});
function useResetCache(data) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current != null) ref.current.resetAfterIndex(0, true);
  }, [data]);
  return ref;
}
const ListboxComponent = React.forwardRef((props, ref) => {
  const { children, ...other } = props;
  const itemData = React.Children.toArray(children);
  const itemCount = itemData.length;
  const itemSize = 48;
  const getChildSize = () => itemSize;
  const getHeight = () => (itemCount > 8 ? 8 * itemSize : itemCount * itemSize);
  const gridRef = useResetCache(itemCount);
  return (
    <div ref={ref}>
      <OuterElementContext.Provider value={other}>
        <VariableSizeList
          itemData={itemData}
          height={getHeight() + 2 * LISTBOX_PADDING}
          width="100%"
          ref={gridRef}
          outerElementType={OuterElementType}
          innerElementType="ul"
          itemSize={getChildSize}
          overscanCount={5}
          itemCount={itemCount}
        >
          {renderRow}
        </VariableSizeList>
      </OuterElementContext.Provider>
    </div>
  );
});

// =====================================================
//  Utilitaires
// =====================================================
const getSourceAvatarUrl = (source) => (!source ? '/assets/default.png' : `/assets/${source}.png`);

const getDecimalAge = (dob) => {
  if (!dob) return '';
  const now = new Date();
  const birthDate = new Date(dob);
  const diffYears = (now - birthDate) / (1000 * 60 * 60 * 24 * 365);
  return diffYears < 0 ? '' : diffYears.toFixed(1);
};
const getShortNationality = (nat) => {
  if (!nat) return '';
  const match = nat.match(/\((.*?)\)/);
  return match ? match[1] : nat;
};

// =====================================================
//  Parser & nettoyage HTML
// =====================================================
function parseHtmlContent(htmlString) {
  if (!htmlString) return null;
  const getTweetIdFromUrl = (url) => {
    const match = url.match(/status\/(\d+)/);
    return match ? match[1] : null;
  };
  return parse(htmlString, {
    replace: (domNode) => {
      // Supprime images
      if (domNode.name === 'img') return null;

      // Iframes YouTube
      if (domNode.name === 'iframe' && domNode.attribs) {
        const src = domNode.attribs.src || '';
        if (src.includes('youtube.com') || src.includes('youtu.be')) {
          return (
            <Box sx={{ my: 2, display: 'flex', justifyContent: 'center' }}>
              <Box sx={{ position: 'relative', width: '100%', maxWidth: 800, pt: '56.25%' }}>
                <ReactPlayer
                  url={src}
                  controls
                  width="100%"
                  height="100%"
                  style={{ position: 'absolute', top: 0, left: 0 }}
                />
              </Box>
            </Box>
          );
        }
        return null;
      }

      // Liens YouTube / Tweet
      if (domNode.name === 'a' && domNode.attribs?.href) {
        const href = domNode.attribs.href;
        if (href.includes('youtube.com') || href.includes('youtu.be')) {
          return (
            <Box sx={{ my: 2, display: 'flex', justifyContent: 'center' }}>
              <Box sx={{ position: 'relative', width: '100%', maxWidth: 800, pt: '56.25%' }}>
                <ReactPlayer
                  url={href}
                  controls
                  width="100%"
                  height="100%"
                  style={{ position: 'absolute', top: 0, left: 0 }}
                />
              </Box>
            </Box>
          );
        }
        if (href.includes('twitter.com') && href.includes('/status/')) {
          const tweetId = getTweetIdFromUrl(href);
          if (tweetId) {
            return (
              <Box sx={{ my: 2, display: 'flex', justifyContent: 'center' }}>
                <Tweet tweetId={tweetId} options={{ width: '100%' }} />
              </Box>
            );
          }
        }
      }

      return undefined;
    },
  });
}

// =====================================================
//  Carte mobile (News)
// =====================================================
const MobileNewsCard = ({ news, onEdit, onDelete, open, onToggle, t, translateFromDB }) => {
  const theme = useTheme();
  const decimalAge = getDecimalAge(news.player_date_naissance);
  const shortNat = getShortNationality(news.player_nationalite);

  return (
    <Card sx={{ mb: 2, boxShadow: theme.shadows[2], maxWidth: '100%' }}>
      <CardHeader
        avatar={<Avatar src={getPlayerPhotoUrl(news.player_id)} alt={news.player_nom} />}
        title={
          <Typography variant="h6" sx={{ bgcolor: '#e0f7fa', p: 0.5, borderRadius: 1 }}>
            {news.title}
          </Typography>
        }
        subheader={new Date(news.updated_at).toLocaleDateString()}
        action={
          <IconButton onClick={() => onToggle(news.id)}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        }
      />
      <Collapse in={open} timeout="auto" unmountOnExit>
        <CardContent
          sx={{
            overflow: 'hidden',           // <= empêche le débordement
            overflowWrap: 'anywhere',
            wordBreak: 'break-word',
            whiteSpace: 'pre-wrap',
            width: '100%',
            p: 2,
          }}
        >
          {/* Infos joueur */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              <strong>{t('news.player')}:</strong>{' '}
              <Link to={`/players/${news.player_id}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                {news.player_nom}
              </Link>
            </Typography>
            <Typography variant="subtitle1" gutterBottom>
              <strong>{t('news.position')}:</strong> {translateFromDB(news.player_poste)}
            </Typography>
            <Typography variant="subtitle1" gutterBottom>
              <strong>{t('news.age')}:</strong> {news.player_date_naissance ? `${decimalAge} ${t('news.years')}` : 'N/D'}
            </Typography>
            <Typography variant="subtitle1" gutterBottom>
              <strong>{t('news.nationality')}:</strong> {shortNat}
            </Typography>
            {/* Competiciones */}
            {news.player_competitions_detail && news.player_competitions_detail.length > 0 ? (
              <Box sx={{ mb: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                  <strong>{t('news.team')}:</strong>
                </Typography>
                {(() => {
                  const equipesMap = new Map();
                  news.player_competitions_detail.forEach(comp => {
                    if (!equipesMap.has(comp.equipe_id)) {
                      equipesMap.set(comp.equipe_id, {
                        equipe_id: comp.equipe_id,
                        equipe_nom: comp.equipe_nom,
                        competitions: []
                      });
                    }
                    equipesMap.get(comp.equipe_id).competitions.push({
                      competition_id: comp.competition_id,
                      competition_nom: comp.competition_nom
                    });
                  });

                  return Array.from(equipesMap.values()).map((equipe, index) => (
                    <Box key={index} sx={{ ml: 2, mb: 0.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                          component="img"
                          src={`${process.env.REACT_APP_MEDIA_URL}/equipes/${equipe.equipe_id}.jpg`}
                          sx={{ width: 20, height: 20, borderRadius: '4px' }}
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                        <Typography variant="body2">{equipe.equipe_nom}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', ml: 3 }}>
                        {equipe.competitions.map((comp, idx) => (
                          <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                            <Box
                              component="img"
                              src={`${process.env.REACT_APP_MEDIA_URL}/competitions/${comp.competition_id}.jpg`}
                              sx={{ width: 16, height: 16, borderRadius: '2px' }}
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                            <Typography variant="caption">{comp.competition_nom}</Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  ));
                })()}
              </Box>
            ) : (
              <Typography variant="subtitle1" gutterBottom>
                <strong>{t('news.team')}:</strong> {news.player_team || 'N/D'}
              </Typography>
            )}
          </Box>

          {/* Source */}
          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
            <Typography variant="subtitle1" sx={{ mr: 1, fontWeight: 'bold' }}>
              {t('news.source')}:
            </Typography>
            <Tooltip title={news.source || ''}>
              <Avatar src={getSourceAvatarUrl(news.source)} alt={news.source} sx={{ width: 40, height: 40 }} />
            </Tooltip>
          </Box>

          {/* Contenu */}
          <Box
            sx={{
              bgcolor: '#FFECB3',
              p: 2,
              borderRadius: 1,
              mt: 1,
              mb: 2,
              overflow: 'hidden',
              overflowWrap: 'anywhere',
              wordBreak: 'break-word',
              whiteSpace: 'pre-wrap',
              width: '100%',
            }}
          >
            {parseHtmlContent(news.content)}
          </Box>
        </CardContent>
        <CardActions sx={{ justifyContent: 'flex-end', p: 2 }}>
          {news.url && (
            <Button size="small" startIcon={<OpenInNewIcon />} onClick={() => window.open(news.url, '_blank')}>
              {t('news.viewArticle')}
            </Button>
          )}
          <Button size="small" startIcon={<EditIcon />} onClick={() => onEdit(news)}>
            {t('common.edit')}
          </Button>
          <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={() => onDelete(news)}>
            {t('common.delete')}
          </Button>
        </CardActions>
      </Collapse>
    </Card>
  );
};

// =====================================================
//  Tiroir (filters) mobile (inchangé)
// =====================================================
const MobileFilterDrawer = ({
  open,
  onClose,
  players,
  sources,
  selectedPlayer,
  selectedSource,
  onPlayerChange,
  onSourceChange,
  t,
}) => (
  <SwipeableDrawer
    anchor="bottom"
    open={open}
    onClose={onClose}
    onOpen={() => {}}
    sx={{
      '& .MuiDrawer-paper': { borderTopLeftRadius: 16, borderTopRightRadius: 16, maxHeight: '80vh' },
    }}
  >
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6">{t('news.filters')}</Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Divider sx={{ mb: 2 }} />
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <FormControl fullWidth>
            <Autocomplete
              options={players}
              getOptionLabel={(p) => p.nom}
              value={selectedPlayer}
              onChange={onPlayerChange}
              ListboxComponent={ListboxComponent}
              renderInput={(params) => <TextField {...params} label={t('news.selectPlayer')} variant="outlined" />}
              renderOption={(props, player) => (
                <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center' }}>
                  <Avatar src={getPlayerPhotoUrl(player.id)} alt={player.nom} sx={{ mr: 1, width: 32, height: 32 }} />
                  {player.nom}
                </Box>
              )}
              clearOnEscape
            />
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <FormControl fullWidth>
            <Autocomplete
              options={sources}
              getOptionLabel={(s) => s}
              value={selectedSource}
              onChange={onSourceChange}
              ListboxComponent={ListboxComponent}
              renderInput={(params) => <TextField {...params} label={t('news.selectSource')} variant="outlined" />}
              renderOption={(props, sourceOption) => (
                <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center' }}>
                  <Avatar src={getSourceAvatarUrl(sourceOption)} alt={sourceOption} sx={{ mr: 1, width: 32, height: 32 }} />
                  {sourceOption}
                </Box>
              )}
              clearOnEscape
              freeSolo
            />
          </FormControl>
        </Grid>
      </Grid>
    </Box>
  </SwipeableDrawer>
);

// =====================================================
//  Composant principal News
// =====================================================
const News = () => {
  const { t } = useTranslation();
  const { translateFromDB } = usePositions(t);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [newsData, setNewsData] = useState([]);
  const [allNewsData, setAllNewsData] = useState([]);

  const [players, setPlayers] = useState([]);
  const [sources, setSources] = useState([]);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [selectedSource, setSelectedSource] = useState('');

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);

  const [openFilterDrawer, setOpenFilterDrawer] = useState(false);
  const [openRows, setOpenRows] = useState({});

  const [loading, setLoading] = useState(true);

  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [currentNews, setCurrentNews] = useState(null);
  const [editedTitle, setEditedTitle] = useState('');
  const [editedContent, setEditedContent] = useState('');
  const [editedSource, setEditedSource] = useState('');
  const [editedUrl, setEditedUrl] = useState('');

  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [newsToDelete, setNewsToDelete] = useState(null);

  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const section = process.env.REACT_APP_SECTION;

  // --- Chargement des données -------------------------
  useEffect(() => {
    const fetchAllData = async () => {
      try {
        setLoading(true);
        const [resNews, resPlayers, resSources] = await Promise.all([
          api.get('/api/news'),
          api.get('/api/players/select'),
          api.get('/api/news/sources'),
        ]);

        const news = resNews.data || [];
        setAllNewsData(news);
        setNewsData(news);

        const _players = Array.isArray(resPlayers.data) ? resPlayers.data : resPlayers.data.players || [];
        setPlayers(_players);

        setSources(resSources.data || []);
      } catch (err) {
        console.error('Error al cargar los datos (noticias):', err);
      } finally {
        setLoading(false);
      }
    };
    fetchAllData();
  }, [backendUrl, section]);

  // --- Filtrage local ---------------------------------
  useEffect(() => {
    let filtered = allNewsData;
    if (selectedPlayer) filtered = filtered.filter((n) => n.player_id === selectedPlayer.id);
    if (selectedSource) filtered = filtered.filter(
      (n) => n.source && n.source.toLowerCase().includes(selectedSource.toLowerCase()),
    );
    setNewsData(filtered);
    setPage(0);
  }, [allNewsData, selectedPlayer, selectedSource]);

  // --- Pagination helpers -----------------------------
  const totalResults = newsData.length;
  const paginatedData = React.useMemo(
    () => (rowsPerPage <= 0 ? newsData : newsData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)),
    [newsData, page, rowsPerPage],
  );
  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };
  const handleChangeMobilePage = (_, value) => setPage(value - 1);
  const toggleRow = (id) => setOpenRows((prev) => ({ ...prev, [id]: !prev[id] }));

  // --- CRUD : Edit ------------------------------------
  const handleEditClick = (newsItem) => {
    setCurrentNews(newsItem);
    setEditedTitle(newsItem.title || '');
    setEditedContent(newsItem.content || '');
    setEditedSource(newsItem.source || '');
    setEditedUrl(newsItem.url || '');
    setOpenEditDialog(true);
  };
  const handleEditSave = async () => {
    if (!currentNews) return;
    try {
      const transformedContent = editedContent
        .replace(/<p><br><\/p>/g, '<br>')
        .replace(/<p>/g, '')
        .replace(/<\/p>/g, '<br>')
        .replace(/<br><br>/g, '<br>');
      const updateData = { title: editedTitle, content: transformedContent, source: editedSource, url: editedUrl };
      await api.put(`/api/news/${currentNews.id}`, updateData);
      setAllNewsData((prev) => prev.map((n) => (n.id === currentNews.id ? { ...n, ...updateData } : n)));
      setOpenEditDialog(false);
      alert('¡Noticia actualizada con éxito!');
    } catch (err) {
      console.error('Error al actualizar la noticia:', err);
      alert('Error al actualizar la noticia.');
    }
  };

  // --- CRUD : Delete ----------------------------------
  const handleDeleteClick = (newsItem) => {
    setNewsToDelete(newsItem);
    setOpenDeleteDialog(true);
  };
  const confirmDelete = async () => {
    if (!newsToDelete) return;
    try {
      await api.delete(`/api/news/${newsToDelete.id}`);
      setAllNewsData((prev) => prev.filter((n) => n.id !== newsToDelete.id));
      setOpenDeleteDialog(false);
      alert('¡Noticia eliminada con éxito!');
    } catch (err) {
      console.error('Error al eliminar la noticia:', err);
      alert('Error al eliminar la noticia.');
    }
  };

  // --- UI : Loading -----------------------------------
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  // =====================================================
  //  RENDER
  // =====================================================
  return (
    <Box sx={{ position: 'relative', pb: isMobile ? 7 : 0 }}>
      {/* ---------------- MOBILE ---------------- */}
      {isMobile ? (
        <>
          <Box
            sx={{
              mx: 2, mt: 2, mb: 1, display: 'flex', alignItems: 'center',
              p: 2, bgcolor: theme.palette.primary.main, color: 'white', borderRadius: 1,
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            }}
          >
            <Box
              sx={{
                bgcolor: 'rgba(255,255,255,0.2)', borderRadius: '50%', width: 40, height: 40,
                display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 2,
                fontWeight: 'bold', fontSize: '1.1rem',
              }}
            >
              {totalResults}
            </Box>
            <Typography variant="subtitle1">
              {totalResults <= 1 ? t('news.newsFound') : t('news.newsFoundPlural')}
            </Typography>
          </Box>

          <Box sx={{ p: 2 }}>
            {paginatedData.map((n) => (
              <MobileNewsCard
                key={n.id}
                news={n}
                open={openRows[n.id] || false}
                onToggle={toggleRow}
                onEdit={handleEditClick}
                onDelete={handleDeleteClick}
                t={t}
                translateFromDB={translateFromDB}
              />
            ))}
          </Box>

          {rowsPerPage !== -1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
              <Pagination
                color="primary"
                count={Math.ceil(totalResults / rowsPerPage)}
                page={page + 1}
                onChange={handleChangeMobilePage}
              />
            </Box>
          )}

          <Fab
            color="primary"
            sx={{ position: 'fixed', bottom: 16, right: 16, zIndex: theme.zIndex.fab }}
            onClick={() => setOpenFilterDrawer(true)}
          >
            <FilterListIcon />
          </Fab>

          <MobileFilterDrawer
            open={openFilterDrawer}
            onClose={() => setOpenFilterDrawer(false)}
            players={players}
            sources={sources}
            selectedPlayer={selectedPlayer}
            selectedSource={selectedSource}
            onPlayerChange={(_, v) => setSelectedPlayer(v)}
            onSourceChange={(_, v) => setSelectedSource(v || '')}
            t={t}
          />
        </>
      ) : (
        /* ---------------- DESKTOP ---------------- */
        <>
          {/* Filtres (top) */}
          <Grid container spacing={2} mb={2}>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth>
                <Autocomplete
                  options={players}
                  getOptionLabel={(p) => p.nom}
                  value={selectedPlayer}
                  onChange={(_, v) => setSelectedPlayer(v)}
                  ListboxComponent={ListboxComponent}
                  renderInput={(params) => <TextField {...params} label={t('news.selectPlayer')} variant="outlined" />}
                  renderOption={(props, p) => (
                    <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar src={getPlayerPhotoUrl(p.id)} alt={p.nom} sx={{ mr: 1 }} />
                      {p.nom}
                    </Box>
                  )}
                  clearOnEscape
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Autocomplete
                options={sources}
                getOptionLabel={(s) => s}
                value={selectedSource}
                onChange={(_, v) => setSelectedSource(v || '')}
                ListboxComponent={ListboxComponent}
                renderInput={(params) => <TextField {...params} label={t('news.selectSource')} variant="outlined" />}
                renderOption={(props, s) => (
                  <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar src={getSourceAvatarUrl(s)} alt={s} sx={{ mr: 1 }} />
                    {s}
                  </Box>
                )}
                freeSolo
                clearOnEscape
              />
            </Grid>
          </Grid>

          {/* Compteur */}
          <Box
            sx={{
              mb: 3, display: 'flex', alignItems: 'center', p: 2,
              bgcolor: theme.palette.primary.main, color: 'white', borderRadius: 1,
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            }}
          >
            <Box
              sx={{
                bgcolor: 'rgba(255,255,255,0.2)', borderRadius: '50%', width: 48, height: 48,
                display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 2,
                fontWeight: 'bold', fontSize: '1.2rem',
              }}
            >
              {totalResults}
            </Box>
            <Typography variant="h6">
              {totalResults <= 1 ? t('news.newsFound') : t('news.newsFoundPlural')}
            </Typography>
          </Box>

          {/* Tableau */}
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell align="center">{t('common.date')}</TableCell>
                  <TableCell align="center">{t('news.player')}</TableCell>
                  <TableCell align="center">{t('news.position')}</TableCell>
                  <TableCell align="center">{t('news.age')}</TableCell>
                  <TableCell align="center">{t('news.nationality')}</TableCell>
                  <TableCell align="center">{t('news.team')}</TableCell>
                  <TableCell align="center">{t('news.news')}</TableCell>
                  <TableCell align="center">{t('news.source')}</TableCell>
                  <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>{t('news.seeMore')}</TableCell>
                  <TableCell align="center">{t('common.actions')}</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {paginatedData.map((row) => {
                  const decimalAge = getDecimalAge(row.player_date_naissance);
                  const shortNat = getShortNationality(row.player_nationalite);

                  return (
                    <React.Fragment key={row.id}>
                      <TableRow
                        hover
                        sx={{
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.04)'
                          }
                        }}
                        onClick={(e) => {
                          // Ne pas toggler si on clique sur un lien, bouton ou action
                          if (
                            e.target.closest('a') ||
                            e.target.closest('button') ||
                            e.target.closest('[role="button"]')
                          ) {
                            return;
                          }
                          toggleRow(row.id);
                        }}
                      >
                        {/* FECHAS */}
                        <TableCell align="center">{new Date(row.updated_at).toLocaleDateString()}</TableCell>

                        {/* JUGADOR */}
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Avatar src={getPlayerPhotoUrl(row.player_id)} alt={row.player_nom} sx={{ mr: 1 }} />
                            <Typography variant="body2">
                              <Link
                                to={`/players/${row.player_id}`}
                                style={{ textDecoration: 'none', color: 'inherit' }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                {row.player_nom}
                              </Link>
                            </Typography>
                          </Box>
                        </TableCell>

                        {/* POS / EDAD / NACIONALIDAD / EQUIPO */}
                        <TableCell align="center">{translateFromDB(row.player_poste)}</TableCell>
                        <TableCell align="center">{decimalAge || 'N/D'}</TableCell>
                        <TableCell align="center">{shortNat}</TableCell>
                        <TableCell align="center">
                          {row.player_competitions_detail && row.player_competitions_detail.length > 0 ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                              {(() => {
                                const equipesMap = new Map();
                                row.player_competitions_detail.forEach(comp => {
                                  if (!equipesMap.has(comp.equipe_id)) {
                                    equipesMap.set(comp.equipe_id, {
                                      equipe_id: comp.equipe_id,
                                      equipe_nom: comp.equipe_nom,
                                      competitions: []
                                    });
                                  }
                                  equipesMap.get(comp.equipe_id).competitions.push({
                                    competition_id: comp.competition_id,
                                    competition_nom: comp.competition_nom
                                  });
                                });

                                return Array.from(equipesMap.values()).map((equipe, index) => (
                                  <Box key={index} sx={{ mb: index < equipesMap.size - 1 ? 0.5 : 0 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                      <Box
                                        component="img"
                                        src={`${process.env.REACT_APP_MEDIA_URL}/equipes/${equipe.equipe_id}.jpg`}
                                        sx={{ width: 20, height: 20, borderRadius: '4px' }}
                                        onError={(e) => { e.target.style.display = 'none'; }}
                                      />
                                      <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
                                        {equipe.equipe_nom}
                                      </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.4, flexWrap: 'wrap' }}>
                                      {equipe.competitions.map((comp, idx) => (
                                        <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 0.2 }}>
                                          <Box
                                            component="img"
                                            src={`${process.env.REACT_APP_MEDIA_URL}/competitions/${comp.competition_id}.jpg`}
                                            sx={{ width: 12, height: 12, borderRadius: '2px' }}
                                            onError={(e) => { e.target.style.display = 'none'; }}
                                          />
                                          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                                            {comp.competition_nom}
                                          </Typography>
                                        </Box>
                                      ))}
                                    </Box>
                                  </Box>
                                ));
                              })()}
                            </Box>
                          ) : (
                            row.player_team || 'N/D'
                          )}
                        </TableCell>

                        {/* NOTICIAS (ancien Título) */}
                        <TableCell>
                        <Box sx={{ bgcolor: '#e0f7fa', p: 0.5, borderRadius: 1, display: 'inline-block' }}>
                          {row.title}
                        </Box>
                      </TableCell>

                        {/* FUENTE */}
                        <TableCell align="center">
                          <Tooltip title={row.source || ''}>
                            <Avatar src={getSourceAvatarUrl(row.source)} alt={row.source} sx={{ mx: 'auto' }} />
                          </Tooltip>
                        </TableCell>

                        {/* VER MÁS */}
                        <TableCell align="center">
                          <IconButton
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleRow(row.id);
                            }}
                          >
                            {openRows[row.id] ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                          </IconButton>
                        </TableCell>

                        {/* ACTIONS */}
                        <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                            {row.url && (
                              <IconButton onClick={() => window.open(row.url, '_blank')} size="small">
                                <OpenInNewIcon fontSize="small" />
                              </IconButton>
                            )}
                            <IconButton onClick={() => handleEditClick(row)} color="primary" size="small">
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton onClick={() => handleDeleteClick(row)} sx={{ color: 'red' }} size="small">
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>

                      {/* COLLAPSE : Contenu */}
                      <TableRow>
                        <TableCell colSpan={10} sx={{ p: 0 }}>
                          <Collapse in={openRows[row.id]} timeout="auto" unmountOnExit>
                            <Box m={1}>
                              <Box
                                sx={{
                                  bgcolor: '#FFECB3',
                                  p: 2,
                                  borderRadius: 1,
                                  overflow: 'hidden',
                                  overflowWrap: 'anywhere',
                                  wordBreak: 'break-word',
                                  whiteSpace: 'pre-wrap',
                                  width: '100%',
                                }}
                              >
                                {parseHtmlContent(row.content)}
                              </Box>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>

            <TablePagination
              rowsPerPageOptions={[5, 10, 25, { label: t('common.all'), value: -1 }]}
              component="div"
              count={totalResults}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </TableContainer>
        </>
      )}

      {/* --------- DIALOG D'ÉDITION --------- */}
      <Dialog open={openEditDialog} onClose={() => setOpenEditDialog(false)} fullWidth maxWidth="md">
        <DialogTitle>{t('news.editNews')}</DialogTitle>
        <DialogContent>
          {currentNews && (
            <Box mt={2}>
              <TextField label={t('news.title')} fullWidth sx={{ mb: 2 }} value={editedTitle} onChange={(e) => setEditedTitle(e.target.value)} />
              <TextField label={t('news.source')} fullWidth sx={{ mb: 2 }} value={editedSource} onChange={(e) => setEditedSource(e.target.value)} />
              <TextField label="URL" fullWidth sx={{ mb: 2 }} value={editedUrl} onChange={(e) => setEditedUrl(e.target.value)} />
              <Typography variant="subtitle1">{t('news.content')}:</Typography>
              <ReactQuill theme="snow" value={editedContent} onChange={setEditedContent} placeholder={t('news.editContentPlaceholder')} />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditDialog(false)} color="secondary">
            {t('common.cancel')}
          </Button>
          <Button onClick={handleEditSave} variant="contained">
            {t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* --------- DIALOG SUPPRESSION --------- */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>{t('news.deleteNews')}</DialogTitle>
        <DialogContent>
          <Typography>{t('news.confirmDeleteMessage')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)} color="secondary">
            {t('common.cancel')}
          </Button>
          <Button onClick={confirmDelete} variant="contained" color="primary">
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default News;
